import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Calculator,
  Trash2, MapPin, ExternalLink, Package, X,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  HIGH_BUILD_EPOXY,
  FLAKE_PRODUCTS,
  POLYASPARTIC_SEAL,
  HIGH_BUILD_SYSTEMS,
  FLAKE_COVERAGE,
  HIGH_BUILD_COLOUR_CHART,
} from '@/components/proepoxy/productData';
import { calculateOptimalPacks } from '@/components/proepoxy/utils';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function HighBuildGuidedStep4({ state, priority, setPriority, currentStep, goToStep, highBuildOrderMode, setHighBuildOrderMode, navigate, onExit }) {
  const {
    highBuildAreas, manualOrder,
    showInstallationGuide, setShowInstallationGuide,
    showColourChart, setShowColourChart,
    highBuildSteps,
    addHighBuildArea, removeHighBuildArea, updateHighBuildArea,
    initializeHighBuildProducts,
    calculateHighBuildTotal, getHighBuildItemCount,
    addPrimerItem, updatePrimerItem, removePrimerItem,
    addHighBuildItem, updateHighBuildItem, removeHighBuildItem,
    addFlakeItem, updateFlakeItem, removeFlakeItem,
    updateManualOrder, setManualOrder,
    calculateManualOrderTotal, getManualOrderItemCount,
    updateManualFloorPrepQty,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-[#27292E]">Products</h2>
                    <Button onClick={() => setShowColourChart(true)} variant="outline" className="border-2 border-[#7A0B7A] text-[#7A0B7A]">
                      <ExternalLink className="w-4 h-4 mr-2" />View Colour Chart
                    </Button>
                  </div>
                  <p className="text-gray-600 mb-6">Review and adjust your products. Quantities are calculated based on your selections.</p>

                  {/* Colour Chart Modal */}
                  {showColourChart && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto p-6 relative">
                        <button onClick={() => setShowColourChart(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow">
                          <span className="text-2xl">×</span>
                        </button>
                        <h3 className="text-xl font-bold text-[#27292E] mb-4">Colour Chart</h3>
                        <img src={HIGH_BUILD_COLOUR_CHART} alt="High Build Colour Chart" className="w-full rounded-xl" />
                      </div>
                    </div>
                  )}

                  {/* Per-Area Products */}
                  <div className="space-y-6">
                    {highBuildAreas.map((area, index) => {
                      const system = HIGH_BUILD_SYSTEMS[area.systemType];
                      if (!system) return null;
                      
                      return (
                        <div key={area.id} className="border-2 border-gray-200 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                            <div>
                              <h3 className="font-bold text-[#27292E]">{area.name}</h3>
                              <p className="text-sm text-gray-500">{area.m2}m² • {system.name}</p>
                            </div>
                            <span className="px-3 py-1 bg-[#7A0B7A]/10 text-[#7A0B7A] rounded-full text-sm font-medium">{area.m2}m²</span>
                          </div>

                          {/* Primer Units (if applicable) */}
                          {system.primerCoats > 0 && (
                            <div className="p-4 bg-gray-50 rounded-xl mb-3">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h4 className="font-medium text-[#27292E]">Epoxy Prime Coat</h4>
                                  <p className="text-xs text-gray-500">5kg units (covers 15-20m² each) • {system.primerCoats} coat(s)</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => updateHighBuildArea(area.id, 'primerUnits', Math.max(0, (area.primerUnits || 0) - 1))}
                                    className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="w-12 text-center font-bold text-lg">{area.primerUnits || 0}</span>
                                  <button
                                    onClick={() => updateHighBuildArea(area.id, 'primerUnits', (area.primerUnits || 0) + 1)}
                                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              {/* Primer Colour Selection */}
                              <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-600 mb-1">Primer Colour</label>
                                <select
                                  value={area.primerColor || 'Grey'}
                                  onChange={(e) => updateHighBuildArea(area.id, 'primerColor', e.target.value)}
                                  className="w-full p-2 border border-gray-200 rounded-lg focus:border-[#7A0B7A] focus:ring-0 text-sm"
                                >
                                  {EPOXY_PRIMERS['2_part'].colors.map(color => (
                                    <option key={color} value={color}>{color}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="pt-2 border-t border-gray-200 flex justify-between text-sm">
                                <span className="text-gray-600">{area.primerUnits || 0} × £{HIGH_BUILD_EPOXY.packs[0].price.toFixed(2)} ({area.primerColor || 'Grey'})</span>
                                <span className="font-bold text-[#7A0B7A]">£{((area.primerUnits || 0) * HIGH_BUILD_EPOXY.packs[0].price).toFixed(2)}</span>
                              </div>
                            </div>
                          )}

                          {/* High Build Units */}
                          <div className="p-4 bg-gray-50 rounded-xl mb-3">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="font-medium text-[#27292E]">High Build Epoxy</h4>
                                <p className="text-xs text-gray-500">5kg units (covers 15-20m² each) • {system.highBuildCoats} coat(s)</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => updateHighBuildArea(area.id, 'highBuildUnits', Math.max(0, (area.highBuildUnits || 0) - 1))}
                                  className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-12 text-center font-bold text-lg">{area.highBuildUnits || 0}</span>
                                <button
                                  onClick={() => updateHighBuildArea(area.id, 'highBuildUnits', (area.highBuildUnits || 0) + 1)}
                                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            {/* High Build Colour Selection */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-medium text-gray-600">Epoxy Colour</label>
                                <button
                                  onClick={() => setShowColourChart(true)}
                                  className="text-xs text-[#7A0B7A] hover:underline flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  View Colour Chart
                                </button>
                              </div>
                              <select
                                value={area.highBuildColor || ''}
                                onChange={(e) => updateHighBuildArea(area.id, 'highBuildColor', e.target.value)}
                                className="w-full p-2 border border-gray-200 rounded-lg focus:border-[#7A0B7A] focus:ring-0 text-sm"
                              >
                                <option value="">Select a colour...</option>
                                {HIGH_BUILD_EPOXY.colors.map(color => (
                                  <option key={color.code} value={color.code}>{color.code} - {color.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="pt-2 border-t border-gray-200 flex justify-between text-sm">
                              <span className="text-gray-600">{area.highBuildUnits || 0} × £{HIGH_BUILD_EPOXY.packs[0].price.toFixed(2)} {area.highBuildColor ? `(${area.highBuildColor})` : ''}</span>
                              <span className="font-bold text-[#7A0B7A]">£{((area.highBuildUnits || 0) * HIGH_BUILD_EPOXY.packs[0].price).toFixed(2)}</span>
                            </div>
                          </div>

                          {/* Flakes (if flake floor) */}
                          {system.hasFlakes && (
                            <>
                              {/* Flake Coverage Calculator Note */}
                              <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl mb-4">
                                <p className="text-sm text-amber-800 font-medium">
                                  Based on your {area.m2}m² area, you will need:
                                </p>
                                <ul className="mt-2 text-sm text-amber-700 space-y-1">
                                  <li>• <strong>{Math.ceil(area.m2 * FLAKE_COVERAGE.fullScatter)}kg</strong> of flakes for a <strong>full scatter</strong> installation (500g/m²)</li>
                                  <li>• <strong>{Math.ceil(area.m2 * FLAKE_COVERAGE.partialScatter)}kg</strong> of flakes for a <strong>partial scatter</strong> installation (150g/m²)</li>
                                </ul>
                              </div>
                              
                              <p className="text-sm text-gray-500 mb-3">Note: 1kg and 10kg packs have limited colours. 20kg packs have all colours available.</p>
                              
                              {/* Flake Packs */}
                              <div className="space-y-3 mb-3">
                                {/* 1kg packs */}
                                <div className="p-4 bg-gray-50 rounded-xl">
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <h4 className="font-medium text-[#27292E]">1kg Flakes</h4>
                                      <p className="text-xs text-gray-500">£{FLAKE_PRODUCTS.packs[0].price.toFixed(2)} • Limited colours</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => updateHighBuildArea(area.id, 'flake1kg', Math.max(0, (area.flake1kg || 0) - 1))}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                      >
                                        <Minus className="w-4 h-4" />
                                      </button>
                                      <span className="w-12 text-center font-bold text-lg">{area.flake1kg || 0}</span>
                                      <button
                                        onClick={() => updateHighBuildArea(area.id, 'flake1kg', (area.flake1kg || 0) + 1)}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  {(area.flake1kg || 0) > 0 && (
                                    <select
                                      value={area.flake1kgColor || ''}
                                      onChange={(e) => updateHighBuildArea(area.id, 'flake1kgColor', e.target.value)}
                                      className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-[#7A0B7A] focus:ring-0 text-sm"
                                    >
                                      <option value="">Select colour...</option>
                                      {FLAKE_PRODUCTS.limitedColors.map(color => (
                                        <option key={color.name} value={color.name}>{color.name}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                                {/* 10kg packs */}
                                <div className="p-4 bg-gray-50 rounded-xl">
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <h4 className="font-medium text-[#27292E]">10kg Flakes</h4>
                                      <p className="text-xs text-gray-500">£{FLAKE_PRODUCTS.packs[1].price.toFixed(2)} • Limited colours</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => updateHighBuildArea(area.id, 'flake10kg', Math.max(0, (area.flake10kg || 0) - 1))}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                      >
                                        <Minus className="w-4 h-4" />
                                      </button>
                                      <span className="w-12 text-center font-bold text-lg">{area.flake10kg || 0}</span>
                                      <button
                                        onClick={() => updateHighBuildArea(area.id, 'flake10kg', (area.flake10kg || 0) + 1)}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  {(area.flake10kg || 0) > 0 && (
                                    <select
                                      value={area.flake10kgColor || ''}
                                      onChange={(e) => updateHighBuildArea(area.id, 'flake10kgColor', e.target.value)}
                                      className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-[#7A0B7A] focus:ring-0 text-sm"
                                    >
                                      <option value="">Select colour...</option>
                                      {FLAKE_PRODUCTS.limitedColors.map(color => (
                                        <option key={color.name} value={color.name}>{color.name}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                                {/* 20kg packs */}
                                <div className="p-4 bg-gray-50 rounded-xl">
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <h4 className="font-medium text-[#27292E]">20kg Flakes</h4>
                                      <p className="text-xs text-gray-500">£{FLAKE_PRODUCTS.packs[2].price.toFixed(2)} • All colours</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => updateHighBuildArea(area.id, 'flake20kg', Math.max(0, (area.flake20kg || 0) - 1))}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                      >
                                        <Minus className="w-4 h-4" />
                                      </button>
                                      <span className="w-12 text-center font-bold text-lg">{area.flake20kg || 0}</span>
                                      <button
                                        onClick={() => updateHighBuildArea(area.id, 'flake20kg', (area.flake20kg || 0) + 1)}
                                        className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  {(area.flake20kg || 0) > 0 && (
                                    <select
                                      value={area.flake20kgColor || ''}
                                      onChange={(e) => updateHighBuildArea(area.id, 'flake20kgColor', e.target.value)}
                                      className="w-full p-2 border-2 border-gray-200 rounded-lg focus:border-[#7A0B7A] focus:ring-0 text-sm"
                                    >
                                      <option value="">Select colour...</option>
                                      {FLAKE_PRODUCTS.allColors.map(color => (
                                        <option key={color.name} value={color.name}>{color.name}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              </div>

                              <div className="p-4 bg-gray-50 rounded-xl">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h4 className="font-medium text-[#27292E]">Polyaspartic Seal Coat</h4>
                                    <p className="text-xs text-gray-500">Clear gloss finish</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => updateHighBuildArea(area.id, 'polyasparticUnits', Math.max(0, (area.polyasparticUnits || 0) - 1))}
                                      className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                    >
                                      <Minus className="w-4 h-4" />
                                    </button>
                                    <span className="w-12 text-center font-bold text-lg">{area.polyasparticUnits || 0}</span>
                                    <button
                                      onClick={() => updateHighBuildArea(area.id, 'polyasparticUnits', (area.polyasparticUnits || 0) + 1)}
                                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Running Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getHighBuildItemCount()} item{getHighBuildItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                    </div>
                    <p className="text-3xl font-bold">£{calculateHighBuildTotal().toFixed(2)}</p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button onClick={() => goToStep(3)} variant="outline" className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5">
                    <ArrowLeft className="w-5 h-5 mr-2" />Back
                  </Button>
                  <Button onClick={() => goToStep(5)} className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl">
                    Review Order<ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
